/**
 * BriefingScreen — briefing matinal généré par l'IA via OpenRouter.
 *
 * Au chargement, appelle AIService.generateBriefing avec les tâches courantes.
 * Fallback local si le backend IA n'est pas disponible.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Mail, Phone, Inbox, ArrowRight, Sparkles, RefreshCw } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueCard } from '@components/UniqueCard';
import { UniqueBadge } from '@components/UniqueBadge';
import { useUserStore } from '@store/userStore';
import { useTaskStore } from '@store/taskStore';
import { useNavStore } from '@store/navStore';
import { formatFrenchLongDate } from '@utils/dateUtils';
import { AIService, AIServiceError, type BriefingResult } from '@services/AIService';

export default function BriefingScreen(): JSX.Element {
  const profile = useUserStore((s) => s.profile);
  const tasks = useTaskStore((s) => s.tasks);
  const navigate = useNavStore((s) => s.navigate);

  const emailCount = tasks.filter((t) => t.source === 'email').length;
  const callCount = tasks.filter((t) => t.source === 'call').length;
  const inheritedCount = tasks.filter((t) => t.source === 'inherited').length;
  const autoDetected = tasks
    .filter((t) => t.source !== 'manual' && t.status !== 'done')
    .slice(0, 5);

  const firstName = profile?.firstName ?? 'Utilisateur';

  const [briefing, setBriefing] = useState<BriefingResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBriefing = async () => {
    setLoading(true);
    setError(null);
    try {
      const now = Date.now();
      const result = await AIService.generateBriefing({
        first_name: firstName,
        email_count: emailCount,
        call_count: callCount,
        tasks: tasks
          .filter((t) => t.status !== 'done')
          .slice(0, 8)
          .map((t) => ({
            title: t.title,
            source: t.source,
            due_in_hours: t.dueAt
              ? Math.max(0, (new Date(t.dueAt).getTime() - now) / 3_600_000)
              : null,
            description: t.description,
          })),
      });
      setBriefing(result);
    } catch (err) {
      const msg =
        err instanceof AIServiceError
          ? err.message
          : 'Impossible de joindre le backend IA.';
      setError(msg);
      setBriefing({
        summary: `Bonjour ${firstName}, voici votre résumé du jour. ${emailCount} emails, ${callCount} appels, ${inheritedCount} tâches héritées. Bonne journée !`,
        key_actions: autoDetected.map((t) => t.title),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBriefing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div
        className="rounded-card p-8 mb-8"
        style={{
          background:
            'linear-gradient(135deg, var(--color-primary-glow) 0%, var(--color-white) 60%, var(--color-primary-light) 100%)',
          border: '1px solid var(--color-border)',
        }}
      >
        <p className="text-xs uppercase tracking-widest font-bold text-[color:var(--color-primary-dark)] mb-1 inline-flex items-center gap-1.5">
          <Sparkles size={12} /> Briefing matinal IA
        </p>
        <h1 className="!border-none !pb-0 !text-3xl">Bonjour {firstName} ✦</h1>
        <p className="mt-1 text-sm text-[color:var(--color-text-medium)] capitalize">
          {formatFrenchLongDate()}
        </p>
      </div>

      <UniqueCard variant="glow" padding="lg" className="mb-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="!text-lg !border-none !pb-0 m-0">Résumé généré par l'IA</h2>
          <UniqueButton
            variant="ghost"
            size="sm"
            leftIcon={<RefreshCw size={14} />}
            onClick={loadBriefing}
            isLoading={loading}
          >
            Régénérer
          </UniqueButton>
        </div>
        {loading && !briefing ? (
          <div className="flex items-center gap-3">
            <span className="unique-loader" />
            <p className="text-sm text-[color:var(--color-text-medium)]">
              L'IA rédige votre briefing…
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm leading-relaxed mb-4">{briefing?.summary}</p>
            {briefing && briefing.key_actions.length > 0 && (
              <ul className="space-y-2">
                {briefing.key_actions.map((action, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm"
                  >
                    <span
                      className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: 'var(--color-primary-dark)' }}
                      aria-hidden
                    />
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {error && (
          <p className="mt-4 text-[11px] text-[color:var(--color-text-medium)] italic">
            ⚠ {error} (résumé de secours affiché)
          </p>
        )}
      </UniqueCard>

      <UniqueCard padding="lg" className="mb-8">
        <h2 className="!text-lg !border-none !pb-0 m-0 mb-4">Résumé du jour</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat icon={<Mail size={18} />} label="Emails en attente" value={emailCount} />
          <Stat icon={<Phone size={18} />} label="Appels manqués" value={callCount} />
          <Stat icon={<Inbox size={18} />} label="Tâches héritées" value={inheritedCount} />
        </div>
      </UniqueCard>

      {autoDetected.length > 0 && (
        <>
          <h2 className="!text-lg mb-4">Tâches auto-détectées</h2>
          <div className="flex flex-col gap-3 mb-8">
            {autoDetected.map((t) => (
              <UniqueCard key={t.id} padding="md">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">{t.title}</p>
                    {t.description && (
                      <p className="text-xs text-[color:var(--color-text-medium)] mt-1">
                        {t.description}
                      </p>
                    )}
                  </div>
                  <UniqueBadge
                    tone={
                      t.source === 'email'
                        ? 'email'
                        : t.source === 'call'
                          ? 'call'
                          : 'gold'
                    }
                  >
                    {t.source}
                  </UniqueBadge>
                </div>
              </UniqueCard>
            ))}
          </div>
        </>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
        <UniqueButton variant="secondary" size="lg">
          Ajouter mes objectifs du jour
        </UniqueButton>
        <UniqueButton
          variant="primary"
          size="lg"
          rightIcon={<ArrowRight size={18} />}
          onClick={() => navigate('dashboard')}
        >
          Démarrer ma journée ✦
        </UniqueButton>
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: JSX.Element;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 bg-white rounded-card border border-[color:var(--color-border)] p-4">
      <span
        className="w-10 h-10 rounded-full flex items-center justify-center"
        style={{
          background: 'var(--color-primary-light)',
          color: 'var(--color-primary-dark)',
        }}
      >
        {icon}
      </span>
      <div>
        <p className="text-xs text-[color:var(--color-text-medium)]">{label}</p>
        <p className="text-2xl font-extrabold leading-none">{value}</p>
      </div>
    </div>
  );
}
